import { useState, useEffect } from 'react';
import { 
  Shield, 
  ScanFace, 
  Fingerprint, 
  Key, 
  Download, 
  Trash2, 
  Check, 
  Lock, 
  Zap, 
  Info, 
  Clock, 
  ExternalLink,
  HelpCircle,
  CreditCard,
  Database as DatabaseIcon,
  Play,
  FileDown,
  Smartphone
} from 'lucide-react';
import { BiometricConfig, getBiometricConfig, saveBiometricConfig } from '../services/biometricService';
import { TariffPlan, UserAccount, EnergyDataPoint, BillInvoice } from '../types/energy';
import { Language, translations } from '../i18n/translations';
import { contractStatusLabel } from '../services/portalPlaces';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { 
  getSqliteStats, 
  downloadSqliteDatabase, 
  executeSqliteQuery, 
  SQLiteStats 
} from '../services/sqliteDatabase';
import { saveLocalSyncedBundle, getSqliteSyncedBundle } from '../services/localSyncStorage';
import { QueryExecResult } from 'sql.js';

interface SettingsViewProps {
  account: UserAccount;
  tariff: TariffPlan;
  hourlyData: EnergyDataPoint[];
  invoices: BillInvoice[];
  onTriggerBiometricTest: () => void;
  onResetCache: () => void;
  lang?: Language;
  onOpenInstallModal?: () => void;
}

export function SettingsView({
  account,
  tariff,
  hourlyData,
  invoices,
  onTriggerBiometricTest,
  onResetCache,
  lang = 'ja',
  onOpenInstallModal
}: SettingsViewProps) {
  const { isInstalled } = usePWAInstall();
  const [bioConfig, setBioConfig] = useState<BiometricConfig>(getBiometricConfig());
  const [showPinModal, setShowPinModal] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [pinSuccessToast, setPinSuccessToast] = useState(false);

  // Contract Details Inline Editor State
  const [isEditingContract, setIsEditingContract] = useState(false);
  const [editAddress, setEditAddress] = useState(() => {
    const a = account.serviceAddress || '';
    return a.includes('千代田区1-1') ? '' : a;
  });
  const [editSupplyPointId, setEditSupplyPointId] = useState(() => {
    const s = tariff.mprnNumber || '';
    return s === '0300000000000000000000' ? '' : s;
  });
  const [contractSavedToast, setContractSavedToast] = useState(false);

  // SQLite WASM State
  const [sqliteStats, setSqliteStats] = useState<SQLiteStats | null>(null);
  const [sqlQuery, setSqlQuery] = useState('SELECT id, demand_number, status, plan_name FROM contracted_places;');
  const [queryResults, setQueryResults] = useState<QueryExecResult[] | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [showSqlConsole, setShowSqlConsole] = useState(false);

  useEffect(() => {
    getSqliteStats().then(setSqliteStats).catch(() => {});
  }, []);

  const handleRefreshStats = () => {
    getSqliteStats().then(setSqliteStats).catch(() => {});
  };

  const handleRunQuery = async () => {
    if (!sqlQuery.trim()) return;
    setQueryError(null);
    try {
      const results = await executeSqliteQuery(sqlQuery);
      setQueryResults(results);
      handleRefreshStats();
    } catch (err: any) {
      setQueryError(err?.message || String(err));
      setQueryResults(null);
    }
  };

  const handleDownloadDb = async () => {
    await downloadSqliteDatabase();
  };

  const t = translations[lang];

  const handleToggleBio = (checked: boolean) => {
    const updated = { ...bioConfig, enabled: checked };
    setBioConfig(updated);
    saveBiometricConfig(updated);
  };

  const handleSetBioType = (type: BiometricConfig['biometricType']) => {
    const updated = { ...bioConfig, biometricType: type };
    setBioConfig(updated);
    saveBiometricConfig(updated);
  };

  const handleSetAutoLock = (mins: number) => {
    const updated = { ...bioConfig, autoLockMinutes: mins };
    setBioConfig(updated);
    saveBiometricConfig(updated);
  };

  const handleSavePin = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPin.length === 4 && /^\d+$/.test(newPin)) {
      const updated = { ...bioConfig, pinCode: newPin };
      setBioConfig(updated);
      saveBiometricConfig(updated);
      setShowPinModal(false);
      setNewPin('');
      setPinSuccessToast(true);
      setTimeout(() => setPinSuccessToast(false), 2000);
    }
  };

  const handleExportCsv = () => {
    const header = lang === 'ja'
      ? '時間帯,使用電力量(kWh),推定電気料金(円),ピーク区分\n'
      : 'Timestamp,Usage(kWh),EstimatedCost(JPY),PeakType\n';
    const rows = hourlyData.map((d) => `${d.label},${d.kwh},${d.cost},${d.isPeak ? (lang === 'ja' ? 'ピーク' : 'Peak') : (lang === 'ja' ? '夜間・通常' : 'Standard')}`).join('\n');
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `symenergy_usage_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 pb-8">
      {/* PWA Home Screen Installation Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white">{t.pwaInstallHeading}</h3>
                {isInstalled && (
                  <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-950/80 border border-emerald-800/60 rounded px-1.5 py-0.5 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Standalone
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{t.pwaInstallSubheading}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-white">
                {isInstalled
                  ? (lang === 'ja' ? 'インストール状態: 完了' : 'Status: Installed')
                  : (lang === 'ja' ? 'インストール状態: 未完了（ブラウザ表示中）' : 'Status: Running in Browser')}
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              {isInstalled
                ? (lang === 'ja' ? 'ホーム画面から全画面アプリとして起動できます。' : 'App is active in standalone fullscreen mode.')
                : (lang === 'ja' ? 'ホーム画面に追加すると、アドレスバーのない全画面アプリとして高速起動します。' : 'Install to your home screen for instant access without browser toolbars.')}
            </p>
          </div>

          <button
            type="button"
            onClick={onOpenInstallModal}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 shrink-0 cursor-pointer ${
              isInstalled
                ? 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700'
                : 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-slate-950 shadow-teal-950/50'
            }`}
          >
            <Download className="w-4 h-4" />
            <span>{isInstalled ? (lang === 'ja' ? 'アプリ情報 / 手順' : 'App Details / Guide') : t.pwaInstallBtn}</span>
          </button>
        </div>
      </div>

      {/* Biometric Security Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">{t.securityHeading}</h3>
            <p className="text-xs text-slate-400">{t.securitySubheading}</p>
          </div>
        </div>

        {/* Master Biometric Toggle */}
        <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-slate-200">{t.requireBioLaunch}</span>
            <p className="text-[11px] text-slate-400">{t.requireBioLaunchDesc}</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={bioConfig.enabled}
              onChange={(e) => handleToggleBio(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
          </label>
        </div>

        {/* Method selector */}
        {bioConfig.enabled && (
          <div className="pt-2 space-y-2">
            <span className="text-xs font-medium text-slate-300">{t.sensorType}</span>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button
                type="button"
                onClick={() => handleSetBioType('face_id')}
                className={`p-3 rounded-2xl border flex items-center gap-2 transition-colors ${
                  bioConfig.biometricType === 'face_id'
                    ? 'bg-teal-500/15 border-teal-500/40 text-teal-300'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <ScanFace className="w-4 h-4 text-teal-400" />
                <span className="font-semibold">{t.faceId}</span>
              </button>

              <button
                type="button"
                onClick={() => handleSetBioType('touch_id')}
                className={`p-3 rounded-2xl border flex items-center gap-2 transition-colors ${
                  bioConfig.biometricType === 'touch_id'
                    ? 'bg-teal-500/15 border-teal-500/40 text-teal-300'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                <Fingerprint className="w-4 h-4 text-teal-400" />
                <span className="font-semibold">{t.touchId}</span>
              </button>
            </div>
          </div>
        )}

        {/* Auto Lock Timer */}
        <div className="pt-2 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-300">
            <Clock className="w-4 h-4 text-slate-500" />
            <span>{t.autoLockTimer}</span>
          </div>
          <select
            value={bioConfig.autoLockMinutes}
            onChange={(e) => handleSetAutoLock(Number(e.target.value))}
            className="bg-slate-950 text-slate-200 border border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none"
          >
            <option value={0}>{t.lockImmediate}</option>
            <option value={1}>{t.lock1m}</option>
            <option value={5}>{t.lock5m}</option>
            <option value={15}>{t.lock15m}</option>
          </select>
        </div>

        {/* Change PIN & Test prompt */}
        <div className="pt-2 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => setShowPinModal(true)}
            className="flex-1 h-10 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs rounded-xl flex items-center justify-center gap-2 transition-colors border border-slate-700/60"
          >
            <Key className="w-3.5 h-3.5 text-teal-400" />
            <span>{t.changePinBtn}</span>
          </button>

          <button
            type="button"
            onClick={onTriggerBiometricTest}
            className="h-10 px-4 bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 font-medium text-xs rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-teal-500/30"
          >
            <Lock className="w-3.5 h-3.5" />
            <span>{t.testSensorBtn}</span>
          </button>
        </div>

        {pinSuccessToast && (
          <p className="text-xs text-emerald-400 flex items-center gap-1 animate-fadeIn">
            <Check className="w-3.5 h-3.5" /> {t.pinSuccessMsg}
          </p>
        )}
      </div>

      {/* Meter & Tariff & Contract Details (Official MyPage items from FAQ) */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-teal-400" />
            <span>{t.contractHeading}</span>
          </h4>
          <button
            type="button"
            onClick={() => setIsEditingContract(!isEditingContract)}
            className="text-xs font-semibold text-teal-400 hover:text-teal-300 bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 rounded-xl px-2.5 py-1 transition-all cursor-pointer"
          >
            {isEditingContract
              ? (lang === 'ja' ? 'キャンセル' : 'Cancel')
              : (lang === 'ja' ? '実データを設定・編集' : 'Edit Real Data')}
          </button>
        </div>

        {isEditingContract && (
          <div className="bg-slate-950 border border-teal-500/40 rounded-2xl p-3.5 space-y-3 text-xs">
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                {lang === 'ja' ? 'ご使用場所住所 (Service Address)' : 'Service Address'}
              </label>
              <input
                type="text"
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                placeholder={lang === 'ja' ? '例: 東京都港区芝公園...' : 'e.g. Minato-ku, Tokyo...'}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-teal-400"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                {lang === 'ja' ? '供給地点特定番号 (22-Digit Supply Point ID)' : '22-Digit Supply Point ID'}
              </label>
              <input
                type="text"
                maxLength={22}
                value={editSupplyPointId}
                onChange={(e) => setEditSupplyPointId(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="03..."
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs font-mono text-teal-300 focus:outline-none focus:border-teal-400"
              />
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('symenergy_saved_service_address', editAddress.trim());
                    localStorage.setItem('symenergy_saved_supply_point_id', editSupplyPointId.trim());
                  }
                  const current = await getSqliteSyncedBundle();
                  const activeId = current.selectedPlaceId;
                  const updatedPlaces = (current.places || []).map((p) =>
                    !activeId || p.id === activeId
                      ? { ...p, address: editAddress.trim(), supplyPointId: editSupplyPointId.trim() }
                      : p
                  );
                  const updatedAcc: UserAccount = {
                    ...current.account,
                    serviceAddress: editAddress.trim(),
                    contractedPlaces: updatedPlaces,
                    tariff: {
                      ...current.account.tariff,
                      mprnNumber: editSupplyPointId.trim()
                    }
                  };
                  await saveLocalSyncedBundle({
                    account: updatedAcc,
                    places: updatedPlaces
                  });
                  setIsEditingContract(false);
                  setContractSavedToast(true);
                  setTimeout(() => {
                    setContractSavedToast(false);
                    window.location.reload();
                  }, 700);
                } catch (err) {
                  console.error(err);
                }
              }}
              className="w-full py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs transition-colors cursor-pointer"
            >
              {lang === 'ja' ? 'SQLite WASMへ保存して反映' : 'Save to SQLite WASM'}
            </button>
          </div>
        )}

        {contractSavedToast && (
          <div className="text-xs text-emerald-400 flex items-center gap-1.5 font-semibold">
            <Check className="w-3.5 h-3.5" />
            <span>{lang === 'ja' ? 'ご使用場所住所と供給地点特定番号を保存しました' : 'Saved real address & Supply Point ID'}</span>
          </div>
        )}

        <div className="bg-slate-950/60 border border-slate-800/80 rounded-2xl p-3.5 space-y-2.5 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-400">{t.providerLabel}</span>
            <span className="text-white font-medium">{lang === 'ja' ? tariff.provider : 'SymEnergy Inc. (シン・エナジー株式会社)'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.accountNoLabel}</span>
            <span className="font-mono text-white">{account.accountNumber || (lang === 'ja' ? '未連携' : 'Not Synced')}</span>
          </div>
          <div className="flex justify-between items-start gap-2">
            <span className="text-slate-400 shrink-0">{lang === 'ja' ? 'ご使用場所住所' : 'Service Address'}</span>
            <span className="text-white font-medium text-right break-all">
              {editAddress || account.serviceAddress || (lang === 'ja' ? '未取得（マイページ同期または上から設定）' : 'Not Synced')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.supplyPointLabel}</span>
            <span className="font-mono text-white">
              {editSupplyPointId || tariff.mprnNumber || (lang === 'ja' ? '未取得（マイページ同期または上から設定）' : 'Not Synced')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.meterSerialLabel}</span>
            <span className="font-mono text-white">{tariff.meterSerial || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.contractCapacityLabel}</span>
            <span className="font-mono text-teal-300 font-bold">{account.contractCapacity || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.paymentMethodLabel}</span>
            <span className="text-slate-200 flex items-center gap-1">
              <CreditCard className="w-3.5 h-3.5 text-teal-400" />
              <span>{account.paymentDetails || (lang === 'ja' ? '未登録' : 'Not Registered')}</span>
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.areaLabel}</span>
            <span className="text-white font-medium">{tariff.area || '-'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.planNameLabel}</span>
            <span className="text-teal-300 font-medium">{lang === 'ja' ? tariff.name : 'SymEnergy 【Night Lifestyle Fit Plan】'}</span>
          </div>

          {/* SymEnergy 3-Tier Pricing Breakdown */}
          <div className="bg-slate-950/80 rounded-2xl p-3 border border-slate-800 space-y-2 mt-2">
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-800/80">
              <span className="text-xs font-bold text-teal-300">{t.tier3PricingTitle}</span>
              <span className="text-[10px] text-slate-400 font-mono">平日/Weekdays</span>
            </div>
            
            <div className="flex items-center justify-between text-xs">
              <div>
                <span className="text-sky-400 font-medium">{lang === 'ja' ? 'デイタイム (昼時間)' : 'Day Time'}</span>
                <span className="text-[10px] text-slate-500 block font-mono">09:00 〜 16:00</span>
              </div>
              <span className="font-mono text-sky-400 font-bold">¥{(tariff.unitRateDay || 26.25).toFixed(2)} / kWh</span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div>
                <span className="text-amber-400 font-medium">{lang === 'ja' ? 'ライフタイム (生活時間)' : 'Life Time'}</span>
                <span className="text-[10px] text-slate-500 block font-mono">06:00〜09:00 / 16:00〜23:00</span>
              </div>
              <span className="font-mono text-amber-400 font-bold">¥{(tariff.unitRateLife || 32.65).toFixed(2)} / kWh</span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div>
                <span className="text-teal-400 font-medium">{lang === 'ja' ? 'ナイトタイム (夜時間・割安)' : 'Night Time (Off-Peak)'}</span>
                <span className="text-[10px] text-slate-500 block font-mono">23:00 〜 翌06:00</span>
              </div>
              <span className="font-mono text-teal-400 font-bold">¥{(tariff.unitRateNight || 18.88).toFixed(2)} / kWh</span>
            </div>

            <p className="text-[10px] text-slate-500 pt-1 border-t border-slate-800/80 leading-tight">
              {t.tierWeekendNotice}
            </p>
          </div>

          <div className="flex justify-between pt-1">
            <span className="text-slate-400">{t.baseFeeLabel}</span>
            <span className="font-mono text-slate-300">
              {lang === 'ja' 
                ? `約¥1,080 / 月 (基本料金 ¥${tariff.standingCharge.toFixed(1)}/日)`
                : `approx. ¥1,080 / mo (Base ¥${tariff.standingCharge.toFixed(1)}/day)`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">{t.contractEndLabel}</span>
            <span className="font-mono text-slate-300">{tariff.contractEndDate || '2027-10-07'}</span>
          </div>
        </div>

        {/* Contracted Places List */}
        {account.contractedPlaces && account.contractedPlaces.length > 0 && (
          <div className="pt-2">
            <span className="text-xs font-semibold text-slate-300 block mb-2">
              {lang === 'ja' ? '登録済みご使用場所一覧' : 'Registered Contract Locations'}
            </span>
            <div className="space-y-2">
              {account.contractedPlaces.map((pl) => (
                <div key={pl.id} className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 text-xs flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">
                        {pl.displayName || pl.demandNumber}
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                        pl.status === '契約中' ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30' : 'bg-slate-800 text-slate-400'
                      }`}>
                        {contractStatusLabel(pl.status, t)}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                      {pl.demandNumber} · {lang === 'ja' ? pl.planName : '【Night】Life Fit Plan'}
                    </p>
                  </div>
                  <span className="text-[11px] font-mono text-teal-400 font-semibold">{pl.contractCapacity}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Official Portal Reference & FAQ Links (From FAQ user requested) */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
        <h4 className="text-sm font-bold text-white flex items-center gap-2">
          <Info className="w-4 h-4 text-teal-400" />
          <span>{t.officialLinksHeading}</span>
        </h4>
        <div className="space-y-2 text-xs">
          <a
            href="https://faq.symenergy.net/--680a73490323e6244e37996c"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-teal-800/40 transition-colors text-slate-200 group"
          >
            <div className="flex items-start gap-2.5">
              <HelpCircle className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-white group-hover:text-teal-400 transition-colors">{t.officialFaqPage}</span>
                <p className="text-[11px] text-slate-500 font-mono">https://faq.symenergy.net/--680a73490323e6244e37996c</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-teal-400 shrink-0" />
          </a>

          <a
            href="https://www.symenergy.net/symenergy/login"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 transition-colors text-slate-200 group"
          >
            <div>
              <span className="font-medium text-white group-hover:text-teal-400 transition-colors">{t.officialPortalLogin}</span>
              <p className="text-[11px] text-slate-500 font-mono">https://www.symenergy.net/symenergy/login</p>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-teal-400 shrink-0" />
          </a>

          <a
            href="https://www.symenergy.net/symenergy/dashboard"
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 hover:bg-slate-800 border border-slate-800 transition-colors text-slate-200 group"
          >
            <div>
              <span className="font-medium text-white group-hover:text-teal-400 transition-colors">{t.officialPortalDash}</span>
              <p className="text-[11px] text-slate-500 font-mono">https://www.symenergy.net/symenergy/dashboard</p>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-teal-400 shrink-0" />
          </a>
        </div>
      </div>

      {/* SQLite WASM Local Database Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center">
              <DatabaseIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-white">{t.sqliteSectionTitle}</h3>
                <span className="text-[10px] font-semibold text-teal-300 bg-teal-950/80 border border-teal-800/60 rounded px-1.5 py-0.5">
                  WASM v3
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{t.sqliteSectionSubtitle}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDownloadDb}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-500/15 hover:bg-teal-500/25 border border-teal-500/30 text-teal-300 text-xs font-semibold rounded-xl transition-colors shadow-sm"
            title="Download .sqlite database file"
          >
            <FileDown className="w-4 h-4" />
            <span className="hidden sm:inline">{t.sqliteDownloadBtn}</span>
            <span className="sm:hidden">.sqlite</span>
          </button>
        </div>

        {/* Database Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-3">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t.sqliteEngineLabel}</span>
            <p className="text-xs font-bold text-teal-400 mt-0.5 font-mono">
              {sqliteStats?.version || 'SQLite 3.x WASM'}
            </p>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-3">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t.sqliteDbSizeLabel}</span>
            <p className="text-xs font-bold text-white mt-0.5 font-mono">
              {sqliteStats?.sizeBytes ? `${(sqliteStats.sizeBytes / 1024).toFixed(1)} KB` : '658 KB'}
            </p>
          </div>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-3 col-span-2 sm:col-span-1">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">
              {lang === 'ja' ? 'ストレージ方式' : 'Storage Engine'}
            </span>
            <p className={`text-xs font-bold mt-0.5 font-mono truncate ${sqliteStats?.isOpfsActive ? 'text-teal-400' : 'text-emerald-400'}`} title={sqliteStats?.storageBackend}>
              {sqliteStats?.storageBackend || 'OPFS (Origin Private FS)'}
            </p>
          </div>
        </div>

        {/* OPFS Location & Flush Indicator */}
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-950/70 border border-teal-900/30 rounded-2xl text-[11px] font-mono">
          <div className="flex items-center gap-2 truncate">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse shrink-0" />
            <span className="text-slate-400 shrink-0">{lang === 'ja' ? 'ファイルパス:' : 'Path:'}</span>
            <span className="text-teal-300 font-semibold truncate">{sqliteStats?.storageLocation || 'opfs:///symenergy_sqlite.db'}</span>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-md bg-teal-500/15 border border-teal-500/30 text-teal-300 font-semibold shrink-0">
            {sqliteStats?.isOpfsActive ? 'OPFS High-Speed' : 'Sync Active'}
          </span>
        </div>

        {/* Relational Tables Badges */}
        <div className="pt-2 border-t border-slate-800/80">
          <span className="text-xs font-semibold text-slate-300 block mb-2">{t.sqliteTablesLabel}</span>
          <div className="flex flex-wrap gap-2">
            {sqliteStats?.tables.map((tbl) => (
              <div 
                key={tbl.name}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-950/80 border border-slate-800 text-[11px] font-mono"
              >
                <span className="text-slate-300">{tbl.name}</span>
                <span className="px-1.5 py-0.2 rounded-md bg-teal-500/20 text-teal-300 font-bold text-[10px]">
                  {tbl.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Interactive SQL Query Console */}
        <div className="pt-2 border-t border-slate-800/80">
          <button
            type="button"
            onClick={() => setShowSqlConsole(!showSqlConsole)}
            className="flex items-center justify-between w-full text-xs font-semibold text-teal-400 hover:text-teal-300 py-1"
          >
            <span className="flex items-center gap-1.5">
              <Play className="w-3.5 h-3.5" />
              <span>{t.sqliteQueryInspector}</span>
            </span>
            <span className="text-[10px] text-slate-500 font-mono">
              {showSqlConsole ? 'Hide ▲' : 'Show ▼'}
            </span>
          </button>

          {showSqlConsole && (
            <div className="mt-3 space-y-2.5 animate-fadeIn">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={sqlQuery}
                  onChange={(e) => setSqlQuery(e.target.value)}
                  placeholder={t.sqliteQueryPlaceholder}
                  className="flex-1 bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-mono text-slate-200 outline-none focus:border-teal-500"
                />
                <button
                  type="button"
                  onClick={handleRunQuery}
                  className="h-9 px-4 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5 transition-colors shrink-0"
                >
                  <Play className="w-3 h-3 fill-slate-950" />
                  <span>{t.sqliteRunQueryBtn}</span>
                </button>
              </div>

              {queryError && (
                <div className="p-2.5 rounded-xl bg-rose-950/60 border border-rose-800/40 text-xs text-rose-300 font-mono">
                  {t.sqliteError}{queryError}
                </div>
              )}

              {queryResults && queryResults.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span className="font-semibold text-white">{t.sqliteQueryResults}</span>
                    <span className="font-mono">{t.sqliteRowsCount.replace('{count}', (queryResults[0].values.length).toString())}</span>
                  </div>

                  <div className="max-h-48 overflow-auto rounded-xl border border-slate-800 bg-slate-950">
                    <table className="w-full text-[11px] text-left border-collapse font-mono">
                      <thead className="bg-slate-900 text-slate-400 sticky top-0 border-b border-slate-800">
                        <tr>
                          {queryResults[0].columns.map((col, idx) => (
                            <th key={idx} className="py-1.5 px-2.5 whitespace-nowrap">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {queryResults[0].values.map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-slate-800/30">
                            {row.map((val, cIdx) => (
                              <td key={cIdx} className="py-1.5 px-2.5 text-slate-300 whitespace-nowrap">
                                {val === null ? <span className="text-slate-600">NULL</span> : String(val)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data Export & Reset */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
        <h4 className="text-sm font-bold text-white">{t.dataManageHeading}</h4>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={handleExportCsv}
            className="flex-1 h-10 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium rounded-xl flex items-center justify-center gap-2 transition-colors border border-slate-700/60"
          >
            <Download className="w-3.5 h-3.5 text-slate-400" />
            <span>{t.exportCsvBtn}</span>
          </button>

          <button
            type="button"
            onClick={onResetCache}
            className="h-10 px-4 bg-rose-950/40 hover:bg-rose-900/40 text-rose-300 text-xs font-medium rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-rose-800/40"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>{t.resetCacheBtn}</span>
          </button>
        </div>
      </div>

      {/* About Unofficial App */}
      <div className="p-4 rounded-2xl bg-slate-950/40 border border-slate-800/60 text-xs text-slate-400 space-y-1">
        <div className="flex items-center gap-1.5 text-slate-300 font-medium">
          <Info className="w-3.5 h-3.5 text-teal-400" />
          <span>{t.aboutHeading}</span>
        </div>
        <p className="text-[11px] leading-relaxed">
          {t.aboutAppDesc}
        </p>
      </div>

      {/* PIN Change Modal */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-xs bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-2xl">
            <h4 className="text-sm font-bold text-white mb-1">{t.pinModalTitle}</h4>
            <p className="text-xs text-slate-400 mb-3">
              {t.pinModalDesc}
            </p>

            <form onSubmit={handleSavePin} className="space-y-3">
              <input
                type="password"
                maxLength={4}
                autoFocus
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••"
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-2.5 text-center text-lg font-mono tracking-widest text-white outline-none focus:border-teal-500"
              />

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={newPin.length !== 4}
                  className="flex-1 h-10 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs rounded-xl disabled:opacity-50"
                >
                  {t.savePinBtn}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPinModal(false)}
                  className="px-3 h-10 bg-slate-800 text-slate-300 text-xs rounded-xl"
                >
                  {t.cancelBtn}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
